'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export const useToggle = (
  initialValue: boolean = false
): [boolean, () => void] => {
  const [state, setState] = useState(initialValue);

  const updateState = () => {
    setState(!state);
  };
  return [state, updateState];
};

export const useToggleRef = (
  initialValue: boolean = false
): [boolean, () => void] => {
  const ref = useRef(initialValue);
  const [state, setState] = useState(ref.current);

  const updateState = () => {
    setState(!state);
    ref.current = !state;
  };
  return [state, updateState];
};

export const useDebounce = (value: any, delay: number = 250) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
};

export const useUrlState = <T>(
  paramName: string,
  defaultValue: T,
  serialize: (value: T) => string = JSON.stringify,
  deserialize: (value: string) => T = JSON.parse
): [T, (t: T) => void] => {
  // Initialize state from URL or default value
  const [state, setState] = useState<T>(() => {
    // Only access window on client side
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const value = params.get(paramName);
      return value ? deserialize(value) : defaultValue;
    }

    return defaultValue;
  });

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (state === defaultValue) {
      params.delete(paramName);
    } else {
      params.set(paramName, serialize(state));
    }

    // Construct new URL
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;

    // Update URL without adding to browser history
    window.history.replaceState({}, '', newUrl);

    // Cleanup function to remove param when component unmounts
    return () => {
      const params = new URLSearchParams(window.location.search);
      params.delete(paramName);
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    };
  }, [state, defaultValue]);

  // Memoized setter function
  const setUrlState = useCallback((newValue: T | ((prev: T) => T)) => {
    setState(newValue);
  }, []);

  return [state, setUrlState] as const;
};

export const useConfirmSwitch = (
  init: boolean = false
): [
  boolean,
  () => void,
  () => void,
  (val: boolean, cb: () => void) => void,
] => {
  const [current, setCurrent] = useState<boolean>(init);
  const [prev, setPrev] = useState<boolean>(init);

  const setChecked = (val: boolean, cb: () => void) => {
    if (val !== current) {
      setPrev(current);
      cb();
    }
  };

  const cancelChange = () => {
    setCurrent(prev);
  };

  const confirmChange = () => {
    setCurrent(!current);
  };

  return [current, cancelChange, confirmChange, setChecked];
};
